import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Heading } from '../atoms/Heading';
import { useAppBarSlots } from '../appBar';
import { phoneQuery, useMediaQuery } from '../hooks/useMediaQuery';
import styles from './PageHeader.module.css';

export interface PageHeaderProps {
  title: ReactNode;
  /** Back link above the title on detail pages, e.g. "‹ Völker" (a router link). */
  back?: ReactNode;
  /** Grey line below the title with two or three key facts, e.g. "Obstwiese · 2 Jahre". */
  meta?: ReactNode;
  /** The page's actions: at most one primary button plus a `…` menu. */
  actions?: ReactNode;
  id?: string;
}

/**
 * The `h1` of a page with its actions (docs/ux-richtlinien.md §4). No subtitle.
 *
 * Phones (#60): title and actions share one row, buttons with an icon show
 * only the icon (the label stays their accessible name). The back link moves
 * into the top bar, which also shows the title once it has scrolled away.
 */
export function PageHeader({ title, back, meta, actions, id }: PageHeaderProps) {
  const slots = useAppBarSlots();
  const phone = useMediaQuery(phoneQuery);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [scrolledAway, setScrolledAway] = useState(false);

  useEffect(() => {
    const heading = headingRef.current;
    if (!heading || !slots.title || typeof IntersectionObserver === 'undefined') return;
    // The top bar covers the top of the page, so the title counts as gone
    // as soon as it slides under it.
    const bar = slots.title.closest('header')?.getBoundingClientRect().height ?? 0;
    const observer = new IntersectionObserver(
      ([entry]) =>
        setScrolledAway(!!entry && !entry.isIntersecting && entry.boundingClientRect.top < bar),
      { rootMargin: `-${Math.round(bar)}px 0px 0px 0px` },
    );
    observer.observe(heading);
    return () => observer.disconnect();
  }, [slots.title]);

  const barBack = phone && back ? slots.back : null;
  return (
    <header className={styles.header}>
      {back && !barBack && <div className={styles.back}>{back}</div>}
      {barBack && createPortal(<div className={styles.barBack}>{back}</div>, barBack)}
      {scrolledAway &&
        slots.title &&
        createPortal(
          <span className={styles.barTitle} aria-hidden="true">
            {title}
          </span>,
          slots.title,
        )}
      <Heading level={1} id={id} className={styles.title} ref={headingRef}>
        {title}
      </Heading>
      {meta && <div className={styles.meta}>{meta}</div>}
      {actions && <div className={styles.actions}>{actions}</div>}
    </header>
  );
}
